import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';

const STATUS_PENDING = 2;
const STATUS_FROZEN = 3;
const STATUS_ROUTED = 4;
const STATUS_COMPLETED = 5;
const STATUS_REJECTED = 6;
const STATUS_CANCELLED = 7;

const STATUS_OPTIONS = [
    { value: 0, label: 'كل الحالات' },
    { value: STATUS_PENDING, label: 'قيد المراجعة' },
    { value: STATUS_FROZEN, label: 'معتمد ومجمَّد' },
    { value: STATUS_ROUTED, label: 'موجَّه لمرفق مستفيد' },
    { value: STATUS_COMPLETED, label: 'مكتمل' },
    { value: STATUS_REJECTED, label: 'مرفوض' },
    { value: STATUS_CANCELLED, label: 'ملغى' },
];

export default {
    name: 'RecycleReview',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'مراجعة طلبات إعادة التدوير متاحة لإدارة الصيدلة والوزارة.');
            this.href('/Home');
            return;
        }
        this.GetInfo(1);
    },

    computed: {
        canViewPage() {
            return !!(this.loginDetails && this.hasInventoryAndStockAccessUi());
        },
        statusOptions() {
            return STATUS_OPTIONS;
        },
        /** إجمالي ما سيُجمَّد فعلياً حسب المتاح الآن (لا حسب المطلوب). */
        approveEffectiveTotal() {
            if (!this.detail) return 0;
            return (this.detail.lines || [])
                .filter((l) => l.lineStatus === 1)
                .reduce((s, l) => s + Number(l.effectiveQuantity || 0), 0);
        },
        shortfallLines() {
            if (!this.detail) return [];
            return (this.detail.lines || []).filter((l) => l.lineStatus === 1 && l.hasShortfall);
        },
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY HH:mm');
        },
        momentDate(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        return {
            filters: { pageNo: 1, pageSize: 25, status: 0, search: '' },
            Info: [],
            pages: 0,

            detail: null,
            detailDialog: false,

            approveDialog: false,
            approveForm: { note: '', beneficiaryBranchesId: null },

            routeDialog: false,
            routeForm: { beneficiaryBranchesId: null, note: '' },

            beneficiaries: [],
            loadingBeneficiaries: false,
        };
    },

    methods: {
        statusLabel(s) {
            const found = STATUS_OPTIONS.find((x) => x.value === s);
            return found ? found.label : '—';
        },
        statusBadge(s) {
            if (s === STATUS_COMPLETED) return 'success';
            if (s === STATUS_FROZEN || s === STATUS_ROUTED) return 'primary';
            if (s === STATUS_PENDING) return 'warning';
            if (s === STATUS_REJECTED || s === STATUS_CANCELLED) return 'danger';
            return 'secondary';
        },
        statusBadgeClass(badge) {
            return 'recycle-badge recycle-badge--' + (badge || 'secondary');
        },
        reasonBadge(reason) {
            return reason === 2 ? 'danger' : (reason === 1 ? 'warning' : 'secondary');
        },

        apiError(err, fallback) {
            const msg = err && err.response && err.response.data ? err.response.data : fallback;
            this.$helper.ShowMessage('error', 'خطأ', msg);
        },

        // ─── القائمة ─────────────────────────────────────────────────────
        GetInfo(pageNo) {
            this.filters.pageNo = pageNo || 1;
            this.$blockUI.Start();
            this.$http.GetRecycleRequests(this.filters)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.Info = (res && res.data && res.data.info) || [];
                    this.pages = (res && res.data && res.data.count) || 0;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.Info = [];
                    this.pages = 0;
                });
        },

        ApplyFilters() {
            this.GetInfo(1);
        },

        ResetFilters() {
            this.filters.status = 0;
            this.filters.search = '';
            this.ApplyFilters();
        },

        // ─── التفاصيل (المقارنة الحية) ───────────────────────────────────
        openDetail(row) {
            if (!row || !row.id) return;
            this.$blockUI.Start();
            this.$http.GetRecycleRequestById(row.id)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.detail = (res && res.data && res.data.info) || null;
                    this.detailDialog = !!this.detail;
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر تحميل تفاصيل الطلب.');
                });
        },

        afterAction(message) {
            this.$helper.ShowMessage('success', 'تم', message);
            this.detailDialog = false;
            this.approveDialog = false;
            this.routeDialog = false;
            this.GetInfo(this.filters.pageNo);
        },

        loadBeneficiaries(requestId) {
            this.loadingBeneficiaries = true;
            return this.$http.GetRecycleBeneficiaries(requestId)
                .then((res) => {
                    this.loadingBeneficiaries = false;
                    this.beneficiaries = (res && res.data && res.data.info) || [];
                })
                .catch(() => {
                    this.loadingBeneficiaries = false;
                    this.beneficiaries = [];
                });
        },

        // ─── الموافقة والتجميد ───────────────────────────────────────────
        openApprove() {
            if (!this.detail) return;
            this.approveForm = { note: '', beneficiaryBranchesId: null };
            this.approveDialog = true;
            this.loadBeneficiaries(this.detail.id);
        },

        submitApprove() {
            this.$blockUI.Start();
            this.$http.ApproveRecycleRequest(this.detail.id, {
                Note: this.approveForm.note || null,
                BeneficiaryBranchesId: this.approveForm.beneficiaryBranchesId || null,
            })
                .then((res) => {
                    this.$blockUI.Stop();
                    this.afterAction((res.data && res.data.message) || 'تم اعتماد الطلب وتجميد الكميات.');
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر اعتماد الطلب.');
                });
        },

        // ─── الرفض ───────────────────────────────────────────────────────
        rejectRequest() {
            this.$prompt('سبب الرفض (إلزامي)', 'رفض الطلب', {
                confirmButtonText: 'رفض',
                cancelButtonText: 'تراجع',
                inputType: 'textarea',
                inputValidator: (v) => (v && v.trim() ? true : 'السبب إلزامي'),
            }).then(({ value }) => {
                this.$blockUI.Start();
                this.$http.RejectRecycleRequest(this.detail.id, { Reason: value.trim() })
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.afterAction((res.data && res.data.message) || 'تم رفض الطلب.');
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر رفض الطلب.');
                    });
            }).catch(() => { });
        },

        // ─── التوجيه لمرفق مستفيد ────────────────────────────────────────
        openRoute() {
            if (!this.detail) return;
            this.routeForm = { beneficiaryBranchesId: this.detail.beneficiaryBranchesId || null, note: '' };
            this.routeDialog = true;
            this.loadBeneficiaries(this.detail.id);
        },

        submitRoute() {
            if (!this.routeForm.beneficiaryBranchesId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر المرفق المستفيد.');
                return;
            }
            this.$blockUI.Start();
            this.$http.RouteRecycleRequest(this.detail.id, {
                BeneficiaryBranchesId: Number(this.routeForm.beneficiaryBranchesId),
                Note: this.routeForm.note || null,
            })
                .then((res) => {
                    this.$blockUI.Stop();
                    this.afterAction((res.data && res.data.message) || 'تم تحديد المرفق المستفيد.');
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر التوجيه.');
                });
        },

        // ─── فك الحجز ────────────────────────────────────────────────────
        releaseRequest() {
            this.$prompt(
                'سيُفك الحجز وتُعاد الكميات إلى رصيد المرفق المرسل. سبب فك الحجز (إلزامي):',
                'فك الحجز',
                {
                    confirmButtonText: 'فك الحجز',
                    cancelButtonText: 'تراجع',
                    inputType: 'textarea',
                    inputValidator: (v) => (v && v.trim() ? true : 'السبب إلزامي'),
                }
            ).then(({ value }) => {
                this.$blockUI.Start();
                this.$http.ReleaseRecycleRequest(this.detail.id, { Reason: value.trim() })
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.afterAction((res.data && res.data.message) || 'تم فك الحجز.');
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر فك الحجز.');
                    });
            }).catch(() => { });
        },

        // ─── تسجيل التسليم الفعلي ────────────────────────────────────────
        markDelivered() {
            this.$prompt('ملاحظة التسليم (اختياري)', 'تسجيل التسليم الفعلي', {
                confirmButtonText: 'تسجيل',
                cancelButtonText: 'تراجع',
                inputType: 'textarea',
            }).then(({ value }) => {
                this.$blockUI.Start();
                this.$http.MarkRecycleDelivered(this.detail.id, { Note: value || null })
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.afterAction((res.data && res.data.message) || 'تم تسجيل التسليم.');
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر تسجيل التسليم.');
                    });
            }).catch(() => { });
        },
    },
};
